<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { usePremiumStore } from '@state/stores/premium'
import { useAnalytics } from '@composables/use-analytics'
import { useKeyControlsService } from '@composables/use-key-controls-service'
import { useLibraryEntry } from '@composables/use-library-entry'
import { PHONE_PREMIUM_TAB, PHONE_TABS } from '@data/nav-destinations'
import { navSectionFor } from '@utils/nav-section'
import TabItem from './tab-item'

const route = useRoute()
const premiumStore = usePremiumStore()
const analyticsService = useAnalytics()
const keyControlsService = useKeyControlsService()
const { onLibraryClick } = useLibraryEntry()

// Phone active rule: mix/artist/show/genre/chart/playlist → Home, results → Search,
// Premium page → Premium, Account/Settings → nothing.
const activeSection = computed(() => navSectionFor(route.name, { phone: true }))
// Logged-out visitors are free listeners too (PRD rule 7).
const tabs = computed(() => (premiumStore.isPremium ? PHONE_TABS : [...PHONE_TABS, PHONE_PREMIUM_TAB]))

function onClick(tab, event) {
    if (tab.id === 'library') onLibraryClick(event)
    if (tab.id === 'premium') analyticsService.premiumUpsellInitiated('tab-bar')
}

// Browser-only (called from an event handler); jsdom and old engines have no matchMedia.
function prefersReducedMotion() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
}

// Re-tap the active tab: Search focuses its field, everything else scrolls to top.
function onRetap(tab) {
    if (tab.id === 'search') keyControlsService.focusSearch()
    else window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' })
}
</script>

<template>
    <nav
        class="tab-bar"
        aria-label="Main"
    >
        <TabItem
            v-for="tab in tabs"
            :key="tab.id"
            :destination="tab"
            :selected="activeSection === tab.id"
            :premium="tab.id === 'premium'"
            @click="onClick(tab, $event)"
            @retap="onRetap(tab)"
        />
    </nav>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// Row 56 (Figma Tab Bar 530:1171); min-, so a larger browser font grows the bar instead of
// wrapping a label. The glass, hairline and safe area belong to the layout's bottom group.
.tab-bar {
    display: flex;
    align-items: stretch;
    min-height: $size-tab-bar-height;
    padding: 0 $space-8;
}
</style>
